// Client for the public stablecoins endpoint, `GET /v1/stablecoins`
// (schema `panther.stablecoins.v1`). The collector builds it every 30 minutes
// from its own chain reads for the largest coins and DefiLlama for the rest;
// the terminal only reads it. Every change is a fraction
// (0.012 = +1.2%) or null, and null means "not measured", never zero.

import { API_BASE, NotPublished, wantsFixture } from "./api";

export const STABLECOINS_SCHEMA = "panther.stablecoins.v1";

/** How far a price sits from its peg: `minor` is a small drift, `depegged` a break. */
export type DepegStatus = "on_peg" | "minor" | "depegged" | "unknown";

export interface StablecoinChainCap {
  chain: string;
  /** The chain in the boards' ids ("ethereum", "bsc"…), C-7b; absent on older boards. */
  chain_id?: string | null;
  market_cap: number | null;
  /** DQ-23: "onchain" when Panther read this chain's supply itself; absent on older boards. */
  source?: SupplySource;
}

/** DQ-23: where a supply figure comes from. */
export type SupplySource = "onchain" | "defillama";

export interface StablecoinIssuerTotal {
  id: string;
  name: string;
  stablecoins: number;
  market_cap: number | null;
  share: number | null;
}

export interface Stablecoin {
  id: string;
  symbol: string;
  name: string;
  /** Peg currency, e.g. "USD". */
  peg: string;
  /** ISO currency the coin is pegged to ("USD", "EUR", "JPY"…); absent before U-25's API change, then read from `peg`. */
  currency?: string;
  /** One unit of the peg currency in USD (EUR ≈ 1.12); null when no rate is known. */
  peg_usd?: number | null;
  /** DefiLlama's peg type, e.g. "peggedUSD". */
  peg_type: string;
  /** DefiLlama's mechanism, e.g. "fiat-backed", "crypto-backed", "algorithmic"; null when unknown. */
  peg_mechanism: string | null;
  price: number | null;
  market_cap: number | null;
  change_24h: number | null;
  change_7d: number | null;
  change_30d: number | null;
  depeg_status: DepegStatus;
  /** Who issues it (C-7b); null when unknown. */
  issuer?: { id: string; name: string } | null;
  /** Where the supply sits, by chain. */
  chains: StablecoinChainCap[];
  /** DQ-23: "onchain" when the coin's total comes from Panther's own chain reads. */
  source?: SupplySource;
  /** DQ-23: DefiLlama's value for the coin, USD, kept as a cross-check. */
  defillama_market_cap?: number | null;
  /** DQ-23: share (0–1) of the coin's value read on chain; null when the coin is on DefiLlama. */
  onchain_share?: number | null;
  /**
   * DQ-16 jump guard: upstream_jump_unconfirmed (market_cap is the last accepted value while DefiLlama's
   * move is unconfirmed) or upstream_chain_held (DefiLlama dropped or misreports the chains in
   * chains_held, kept at their last good value); null when DefiLlama's value is used as it is.
   */
  stale_reason?: string | null;
  /** DefiLlama chain names held at their last good value. */
  chains_held?: string[] | null;
  /** When the hold began (ISO time); absent until the API sends it. */
  held_since?: string | null;
  /** DefiLlama's latest value while held, USD, and its move against market_cap as a fraction. */
  candidate_market_cap?: number | null;
  candidate_change?: number | null;
}

export interface StablecoinChain {
  chain: string;
  /** The chain in the boards' ids, C-7b. */
  chain_id?: string | null;
  market_cap: number | null;
  /** Of the total across chains, as a fraction. */
  share: number | null;
  /** Stablecoins with supply on the chain. */
  stablecoins: number | null;
}

export interface StablecoinsTotals {
  stablecoins: number;
  /** Market cap of every fiat stablecoin, all currencies, in USD. */
  market_cap: number | null;
  change_24h: number | null;
  change_7d: number | null;
  change_30d: number | null;
  depegged: number;
  /** The total split by peg currency, largest first. */
  by_currency?: StablecoinCurrencyTotal[];
  /** The total split by issuer, largest first (C-7b). */
  by_issuer?: StablecoinIssuerTotal[];
  /** DQ-23: coins whose supply was read on chain this run. */
  onchain?: number;
  /** DQ-23: share (0–1) of market_cap read on chain; null when nothing was. */
  onchain_share?: number | null;
}

export interface StablecoinCurrencyTotal {
  currency: string;
  stablecoins: number;
  /** In USD. */
  market_cap: number | null;
  /** Of the all-currency total, as a fraction. */
  share: number | null;
}

export interface StablecoinsHistoryPoint {
  /** ISO timestamp of the reading. */
  t: string;
  market_cap: number | null;
  /** DQ-16: "upstream_unconfirmed" when the day's figure holds an upstream value the jump guard has not confirmed. */
  quality?: string | null;
}

export interface Stablecoins {
  schema: typeof STABLECOINS_SCHEMA;
  slot: number;
  as_of: string;
  generated_at: string;
  source: { name: string; url: string };
  totals: StablecoinsTotals;
  stablecoins: Stablecoin[];
  chains: StablecoinChain[];
  history: StablecoinsHistoryPoint[];
}

/** Reads the stablecoins board; a 404 means the collector has not published one yet. */
export async function fetchStablecoins(signal?: AbortSignal): Promise<Stablecoins> {
  if (import.meta.env.DEV && wantsFixture()) return (await import("./fixtures/stablecoins.fixture")).STABLECOINS_FIXTURE;
  const response = await fetch(`${API_BASE}/v1/stablecoins`, { signal });
  if (response.status === 404) throw new NotPublished();
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  const data = (await response.json()) as Stablecoins;
  if (data.schema !== STABLECOINS_SCHEMA) throw new Error("Unexpected stablecoins format");
  return data;
}
